import React, { useState, useEffect } from 'react';
import { Repeat1, ChevronLeft, ChevronRight, Shuffle, Search, Heart, Music2, Film, TvMinimalPlay } from 'lucide-react';
import { XMarkIcon } from "@heroicons/react/24/outline";
import { toast } from 'react-toastify';
import { useUser } from "../context/UserContext";
import config from '../config';
import utils from "../utils/Utils";
import Layout from '../layout/Layout';
import PlaylistGallery from '../components/PlaylistGallery';
import ImageGallery from '../components/ImageGallery';
import Form from '../components/Form';
import PageTitle from '../components/PageTitle';
import FormField from '../components/FormField';
import YouTubeViewer from '../components/YouTubeViewer';
import ButtonIcon from '../components/ButtonIcon';

export default function YouTubeVideo() {
    const { user, setUser, savePlayCounts, saveSearchKeyword, authService } = useUser();
    const [filespec, setFilespec] = useState();
    const [videoList, setVideoList] = useState([]);
    const [dailyList, setDailyList] = useState([]);
    const [heartList, setHeartList] = useState([]);
    const [crackList, setCrackList] = useState([]);
    const [playlists, setPlaylists] = useState([]);
    const [loading, setLoading] = useState(false);
    const [toastId, setToastId] = useState(null)
    const [currentIndex, setCurrentIndex] = useState(-1);
    const [repeating1, setRepeating1] = useState(user.repeating1);
    const [shuffling, setShuffling] = useState(user.shuffling);
    const [liking, setLiking] = useState(false);
    const [audioing, setAudioing] = useState(false);
    const [videoing, setVideoing] = useState(false);
    const [searching, setSearching] = useState(false);
    const [playlistGalleryMaximized, setPlaylistGalleryMaximized] = useState(false);
    const [clipboardText, setClipboardText] = useState('');

    // Regex: Block OS-invalid characters and control codes
    // Allow empty input or strings without invalid characters
    const invalidCharsRegex = /[\p{C}\\/:*?"<>|]/gu;

    useEffect(() => {
        // console.log(clipboardText);
        if (clipboardText === '') return;

        if (filespec?.url !== clipboardText) {
            const video_id = getVideoId(clipboardText);
            // console.log(video_id);

            if (!video_id || video_id === "") {
                // console.log(video_id);
                return;
            }

            let item = {...filespec};
            item.video_id = undefined;
            updateFilespec(item);

            setTimeout(() => {
                setFilespec((filespec) => ({
                    ...filespec,
                    url: config.videoBaseUrl + video_id,
                    video_id: video_id,
                    title: '',
                }));
            }, 500);
        }
    }, [clipboardText]);

    const readClipboard = async () => {
        try {
            if (!navigator.clipboard) {
                // console.error('Clipboard API not supported');
                return;
            }

            if (!document.hasFocus()) {
                // console.log('Document not focused');
                return;
            }

            const text = await navigator.clipboard.readText();
            // console.log(text);
            setClipboardText(text);

        } catch (error) {
            console.error('Failed to read clipboard:', error);
        }
    };

    const clearClipboard = async () => {
        try {
            await navigator.clipboard.writeText('');
            setClipboardText('');
            
        } catch (error) {
            console.error('Failed to clear clipboard:', error);
        }
    };

    useEffect(() => {
        initPlaylists();

        const timer = setInterval( async () => {
            await readClipboard();
        }, 3000);

        return () => {
            clearInterval(timer);
            savePlayCounts();
            saveSearchKeyword();
        };
    }, []);

    const updateFilespec = (item) => {
        setFilespec((filespec) => ({
            ...filespec,
            url: config.videoBaseUrl + item?.video_id,
            video_id: item?.video_id,
            title: item?.title,
        }));
    };

    const handleRepeat1 = () => {
        if (!repeating1) {
            setShuffling(false);
        }
        setRepeating1(!repeating1);

        setUser((profile) => ({
            ...profile,
            repeating1: !repeating1,
            shuffling: false,
        }));
    };

    const handleShuffle = () => {
        if (!shuffling) {
            setRepeating1(false);
        }
        setShuffling(!shuffling);

        setUser((profile) => ({
            ...profile,
            repeating1: false,
            shuffling: !shuffling,
        }));
    };

    const nextVideo = () => {
        let offset = 1;

        if (repeating1) {
            offset = 0;
        } else if (shuffling) {
            offset = Math.floor(Math.random() * videoList.length);
            if (offset === 0) {
                offset = 1;
            }
        }

        const idx = (currentIndex + offset) % videoList.length;

        if (idx === currentIndex) {
            let item = {...filespec};
            item.video_id = undefined;
            updateFilespec(item);

            setTimeout(() => {
                updateFilespec(videoList[idx]);
                setCurrentIndex((prev) => (prev + offset) % videoList.length);
            }, 500);

        } else {
            updateFilespec(videoList[idx]);
            setCurrentIndex((prev) => (prev + offset) % videoList.length);
        }
    };

    const prevVideo = () => {
        let offset = 1;

        if (repeating1) {
            offset = 0;
        } else if (shuffling) {
            offset = Math.floor(Math.random() * videoList.length);
            if (offset === 0) {
                offset = 1;
            }
        }

        const idx = (currentIndex - offset + videoList.length) % videoList.length;

        if (idx === currentIndex) {
            let item = {...filespec};
            item.video_id = undefined;
            updateFilespec(item);

            setTimeout(() => {
                updateFilespec(videoList[idx]);
                setCurrentIndex((prev) => (prev - offset + videoList.length) % videoList.length);
            }, 500);

        } else {
            updateFilespec(videoList[idx]);
            setCurrentIndex((prev) => (prev - offset + videoList.length) % videoList.length);
        }
    };

    useEffect(() => {
        // console.log(user.user_id);
        setFilespec((filespec) => ({
            ...filespec,
            user_id: user.user_id,
        }));
    }, [user])

    const getDailyList = async (active = true) => {
        try {
            // const response = await axios.get(`${config.apiBaseUrl}daily-videos`, {
            const response = await authService.get(`/daily-videos`, {
                params: {
                    user_id: user.user_id,
                    keyword: user.keyword || '',
                }
            });
            // console.log(response.status);
            // console.log(response.data);
            const thisList = response.data;
            return thisList;

        } catch (error) {
            // console.log(error);
            toast.error("Failed to list new release videos, please try again later");
        }
        return [];
    };

    const getVideoList = async (active = true) => {
        try {
            // const response = await axios.get(`${config.apiBaseUrl}/video-list`, {
            const response = await authService.get(`/video-list`, {
                params: {
                    user_id: user.user_id,
                    active: active,
                }
            });
            // console.log(response.status);
            // console.log(response.data);
            const thisList = response.data;
            return thisList;

        } catch (error) {
            // console.log(error);
            toast.error("Failed to list downloaded videos, please try again later");
        }
        return [];
    };

    const getPlaylists = async () => {
        try {
            // console.log('hola', authService.getAccessToken());

            // const response = await axios.get(`${config.apiBaseUrl}/playlists`, {
            const response = await authService.get(`/playlists`, {
                params: {
                    user_id: user.user_id,
                }
            });
            // console.log(response.status);
            // console.log(response.data);
            const thisList = response.data;
            return thisList;

        } catch (error) {
            // console.log(error);
            toast.error("Failed to list playlist videos, please try again later");
        }
        return [];
    };

    const refreshPlaylists = async () => {
        let thisList = [];

        thisList = await getDailyList();
        setDailyList(thisList);

        thisList = await getVideoList(false);
        setCrackList(thisList);

        thisList = await getVideoList(true);
        setVideoList(thisList);
        setHeartList(thisList);

        if (thisList && filespec) {
            if (thisList?.find(item => item.video_id === filespec?.video_id) === undefined) {
                if (thisList.length > 0) {
                    updateFilespec(thisList[0]);
                } else {
                    handleClear("url");
                }
            }
        }

        const playlists = await getPlaylists();
        if (playlists?.length > 0) {
            playlists?.map(playlist => {
                if (playlist?.playloads?.length > 0) {
                    playlist?.playloads?.map(item => {
                        const idx = thisList.findIndex(heart => heart.video_id === item.video_id);
                        if (idx >= 0) {
                            item.title = thisList[idx].title;
                        }
                    });
                }
            });
        }
        setPlaylists(playlists);
    };

    const loadPlaylist = (playlist_id) => {
        // console.log('loadPlaylist', playlist_id, dailyList, heartList, playlists, crackList);
        let thisList = [];

        if (playlist_id === -2) {
            setVideoList(dailyList);
            thisList = dailyList;
        }
        if (playlist_id === 0) {
            setVideoList(heartList);
            thisList = heartList;
        }
        if (playlist_id > 0 && playlists?.length > 0) {
            // console.log(playlists);
            const idx = playlists?.findIndex(playlist => playlist.id === playlist_id);
            if (idx >= 0) {
                setVideoList(playlists[idx].playloads);
                thisList = playlists[idx].playloads;
            }
        }
        if (playlist_id === -1) {
            setVideoList(crackList);
            thisList = crackList;
        }

        if (thisList.length > 0) {
            const item = thisList[0];          

            setFilespec((filespec) => ({
                ...filespec,
                url: config.videoBaseUrl + item.video_id,
                video_id: item.video_id,
                title: item.title,
            }));
        }

        setUser((profile) => ({
            ...profile,
            playlist: thisList,
        }));
    };

    const initPlaylists = async () => {
        await refreshPlaylists();

        if (!user.playlist_id) {
            setUser((profile) => ({
                ...profile,
                playlist_id: -2,
            }));
        }
        if (!user.repeating1) {
            setUser((profile) => ({
                ...profile,
                repeating1: false,
            }));
        }
        if (!user.shuffling) {
            setUser((profile) => ({
                ...profile,
                shuffling: false,
            }));
        }
    };

    useEffect(() => {
        loadPlaylist(user.playlist_id);
    }, [user.playlist_id, playlists]);

    useEffect(() => {
        const getVideoInfo = async () => {
            // do nothing when no valid video
            // console.log(filespec)
            if (!filespec || (filespec?.video_id??"") === "") return;

            // do nothing when title exists
            // console.log(filespec)
            if ((filespec?.title??"") !== "") return;

            // get existing title
            // console.log(videoList)
            // const checkVideoId = obj => obj.video_id === (filespec.video_id);
            // if (videoList.some(checkVideoId)) return;
            const itemList = videoList.filter(obj => obj.video_id === filespec.video_id);
            if (itemList.length > 0) {
                // console.log(itemList);

                setFilespec((filespec) => ({
                    ...filespec,
                    title: itemList[0].title || itemList[0].video_info.title,
                }));
                // console.log(filespec)
                return;
            }

            // scrape title
            // console.log(filespec)
            try {
                // const response = await axios.get(`${config.apiBaseUrl}/video-info`, {
                const response = await authService.get(`/video-info`, {
                    params: {
                        video_id: filespec.video_id,
                    }
                })
                // console.log(response.data)
                // console.log(response?.data?.title)
                if (response?.data?.title !== "") {
                    setFilespec((filespec) => ({
                        ...filespec,
                        title: response?.data?.title,
                    }));
                }

            } catch (error) {
                toast.info("Failed to get title")
            }
        }
        getVideoInfo();

    }, [filespec]);

    useEffect(() => {
        const idx = videoList.findIndex(item => item.video_id === filespec.video_id);
        setCurrentIndex(idx);
    }, [filespec]);

    useEffect(() => {
        if (loading) {
            const id = toast.loading("Downloading ...", { autoClose: false })
            setToastId(id)
        } else {
            if (toastId !== null) {
                toast.dismiss(toastId)
                setToastId(null)
            }
        }
    }, [loading])

    const getVideoId = (value) => {
        // https://www.youtube.com/watch?v=V-oE7EF-KA8
        let video_id = value.split("&")[0].split("v=")[1]?.trim();

        // https://youtu.be/V-oE7EF-KA8?si=VWzGJVS6NM9ZR1J-
        if (!video_id || video_id === "") {
            video_id = value.split("?")[0].split("be/")[1]?.trim();
        }

        // https://youtube.com/shorts/DLXOuULEQfE?si=cu6qgJO--VskvwfU
        if (!video_id || video_id === "") {
            video_id = value.split("?")[0].split("shorts/")[1]?.trim();
        }

        // https://www.youtube.com/live/6Fz1O29C9fw?si=tLwfTtQwXEd62wr0
        if (!video_id || video_id === "") {
            video_id = value.split("?")[0].split("live/")[1]?.trim();
        }

        // if (!video_id || video_id === "") {
        //     video_id = value;
        // }

        // console.log(video_id);
        return video_id;
    }

    const handleChange = (e) => {
        const { name, value } = e.target;
        // console.log(name, value);

        if (name === "url") {
            const video_id = getVideoId(value);

            setFilespec((filespec) => ({
                ...filespec,
                video_id: video_id,
            }));
        }

        if (name === "title") {
            setFilespec((filespec) => ({
                ...filespec,
                [name]: value,
            }));
            return
        }

        setFilespec((filespec) => ({
            ...filespec,
            [name]: value,
        }));

        if (name === "keyword") {
            setUser((profile) => ({
                ...profile,
                keyword: utils.toTitleCase(value),
            }));
            return
        }
    };

    const handleClear = (param, e) => {
        setFilespec((filespec) => ({
            ...filespec,
            [param]: "",
        }));

        if (param === "url") {
            setFilespec((filespec) => ({
                ...filespec,
                video_id: "",
                title: "",
            }));
        }

        if (param === "keyword") {
            setUser((profile) => ({
                ...profile,
                keyword: "",
            }));
        }
    };
    
    const handleUpdate = async (e, param) => {
        e.preventDefault();

        if ((filespec?.video_id??"") === "") {
            toast.info("Invalid video link")
            return
        }

        if ((filespec?.title??"") === "") {
            toast.info("Please enter a title")
            return
        }

        if (invalidCharsRegex.test(filespec?.title??"")) {
            toast.info("Invalid characters in Title")
            return
        }

        try {
            // console.log(filespec, param);
            // const response = await axios.put(`${config.apiBaseUrl}/video-update/${filespec.video_id}`, 
            const response = await authService.put(`/video-update/${filespec.video_id}`, 
                {...filespec,
                    active: param,
                })
            // console.log(response.status)

            if (response.status === 200) {
                toast.success("Title updated successfully")
                refreshPlaylists();
                setLiking(false);

            } else {
                toast.error("Failed to update title")
            }

        } catch(error) {
            toast.error("Failed to update title")
        }
    };

    const handleDownload = async (e, param) => {
        e.preventDefault();
        // console.log(filespec)
        // console.log(param)
        // console.log(`${config.apiBaseUrl}/download-${param}`)

        if ((filespec?.url??"") === "") {
            toast.info("Please enter a URL")
            return
        }

        if ((filespec?.video_id??"") === "") {
            toast.info("Invalid video link")
            return
        }

        setLoading(true);
        try {
            // const response = await axios.post(`${config.apiBaseUrl}/download`, 
            const response = await authService.post(`/download`,
                {...filespec,
                    file_type: param,
                },
                {
                    // Ensure the response is treated as a file download
                    responseType: 'blob'
                }
            );
            // console.log(response);
            // console.log(response.headers)
            // console.log(response.data);
            // toast.success(response.data.message ?? "Success");
            // toast.success(response.headers.get('X-File-Path')?? "File path not available");

            // Create a link element to trigger the download
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;

            // Use the filename from Content-Disposition header if possible
            const filename = (
                // response.headers['Content-Disposition']?.split('filename=')[1] || 
                filespec.title || 
                filespec.video_id ||
                "unknown"
            ) + (param === "audio" ? ".mp3" : ".mp4")

            link.setAttribute('download', filename);
            document.body.appendChild(link);
            link.click();
            
            // Clean up
            link.remove();
            window.URL.revokeObjectURL(url);

            toast.success('File downloaded successfully');
            refreshPlaylists();

            if (param === "audio") {
                setAudioing(false);
            }
            if (param === "video") {
                setVideoing(false);
            }

        } catch (error) {
            // toast.error(error.message ?? "Error");
            toast.error('Failed to download file');
        } finally {
            setLoading(false);
        };
    }

    const handleOpenYouTube = (e) => {
        e.preventDefault();

        let thisUrl = config.videoBaseUrl;
        if (filespec?.video_id) {
            thisUrl += filespec?.video_id;
        }

        window.open(
            thisUrl, 
            '_blank',
            'noopener,noreferrer'
        );
    };

    const handleSearch = async () => {
        if (!user.keyword || user.keyword === '') {
            setSearching(false);
            toast.info("Please enter a keyword for search");
            return;
        }

        try {
            saveSearchKeyword();

            let thisList = [];
            thisList = await getDailyList();
            setDailyList(thisList);
            setSearching(false);
            toast.success("Searched keyword successfully");

        } catch {
            toast.error("Failed to search keyword");
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        // await handleUpdate(e, true);
    };

    const handlePlaylistGalleryResize = () => {
        setPlaylistGalleryMaximized(!playlistGalleryMaximized);
    };

    return (
        <Layout>
            <div className='hidden custom-flex'>
                {(videoList?.length > 0) || (crackList?.length > 0)
                    ? <PlaylistGallery
                        dailyList={dailyList}
                        heartList={heartList}
                        playlists={playlists}
                        crackList={crackList}
                        onResize={handlePlaylistGalleryResize}
                        setFilespec={setFilespec}
                        refreshPlaylists={refreshPlaylists}
                    />
                    : <></>
                }

                {!playlistGalleryMaximized
                    ? <ImageGallery images={videoList} setFilespec={setFilespec} />
                    : <></>
                }
                {/* {(videoList?.length > 0) && (!playlistGalleryMaximized)
                    ? <ImageGallery images={videoList} setFilespec={setFilespec} />
                    : <></>
                } */}
            </div>
    
            <div>
                <Form height='min-h-[calc(66vh)]' handleSubmit={handleSubmit}>
                    <div className='flex items-center justify-center'>
                        <button
                            onClick={handleRepeat1}
                            className={`flex w-10 h-10 mx-1 mb-2 backdrop-blur-sm rounded-full items-center justify-center text-orange-400 transition-all duration-300 border border-white/20 hover:scale-110 ${
                                repeating1 
                                ? 'bg-orange-100' 
                                : 'bg-white/10 hover:bg-white/20'
                            }`}
                        >
                            <Repeat1 size={24} />
                        </button>
                        <button
                            onClick={prevVideo}
                            className="flex w-10 h-10 mx-1 mb-2 bg-white/10 backdrop-blur-sm rounded-full items-center justify-center text-orange-400 hover:bg-white/20 transition-all duration-300 border border-white/20 hover:scale-110"
                        >
                            <ChevronLeft size={24} />
                        </button>

                        <div className='mx-4'>
                            <PageTitle pageTitle={"YouTube"} />
                        </div>

                        <button
                            onClick={nextVideo}
                            className="flex w-10 h-10 mx-1 mb-2 bg-white/10 backdrop-blur-sm rounded-full items-center justify-center text-orange-400 hover:bg-white/20 transition-all duration-300 border border-white/20 hover:scale-110"
                        >
                            <ChevronRight size={24}/>
                        </button>
                        <button
                            onClick={handleShuffle}
                            className={`flex w-10 h-10 mx-1 mb-2 backdrop-blur-sm rounded-full items-center justify-center text-orange-400 transition-all duration-300 border border-white/20 hover:scale-110 ${
                                shuffling 
                                ? 'bg-orange-100' 
                                : 'bg-white/10 hover:bg-white/20'
                            }`}
                        >
                            <Shuffle size={24} />
                        </button>
                    </div>

                    {/* <VideoItem
                        video_id={filespec?.video_id}
                        title={filespec?.title}
                    /> */}

                    <YouTubeViewer
                        videoId={filespec?.video_id}
                        onEnd={nextVideo}
                        height={180}
                        width={320}
                    />

                    <div className='mb-2'></div>
                    {/* <ImageItem
                        video_id={filespec?.video_id}
                        title={filespec?.title}
                        alt={filespec?.title}
                    /> */}

                    <FormField
                        label={"URL"}
                        name={"url"}
                        type={"url"}
                        value={filespec?.url}
                        onChange={handleChange}
                        required={true}
                        autoFocus={true}
                        onClear={handleClear.bind(null, "url")}
                    />

                    <FormField
                        label={"Title"}
                        name={"title"}
                        type={"text"}
                        value={filespec?.title}
                        onChange={handleChange}
                        onClear={handleClear.bind(null, "title")}
                    />

                    {/* <ButtonSubmit 
                        loading={loading} 
                        labeling={'Downloading ...'} 
                        label={'Download'} 
                        disabled={loading}
                    /> */}

                    <div className='flex justify-center items-center'>
                        <ButtonIcon
                            icon={Heart}
                            title={'Add to Playlist'}
                            bouncing={liking}
                            onClick={ async (e) => {
                                setLiking(true);
                                await handleUpdate(e, true);
                            }}
                        />
                        <ButtonIcon
                            icon={Music2}
                            title={'Download Audio'}
                            bouncing={audioing}
                            onClick={ async (e) => {
                                setAudioing(true);
                                await handleDownload(e, "audio");
                            }}
                        />
                        <ButtonIcon
                            icon={Film}
                            title={'Download Video'}
                            bouncing={videoing}
                            onClick={ async (e) => {
                                setVideoing(true);
                                await handleDownload(e, "video");
                            }}
                        />
                        <ButtonIcon
                            icon={TvMinimalPlay}
                            title={'Open in YouTube'}
                            onClick={handleOpenYouTube}
                        />

                        {/* <button
                            onClick={handleDownload.bind(null, "audio")}
                            className="flex w-10 h-10 mx-4 mt-2 bg-white/10 backdrop-blur-sm rounded-full items-center justify-center text-orange-400 hover:bg-white/20 transition-all duration-300 border border-white/20 hover:scale-110"
                            title="Download Audio"
                        >
                            <Music2 size={24} />
                        </button>
                        <button
                            onClick={handleDownload.bind(null, "video")}
                            className="flex w-10 h-10 mx-4 mt-2 bg-white/10 backdrop-blur-sm rounded-full items-center justify-center text-orange-400 hover:bg-white/20 transition-all duration-300 border border-white/20 hover:scale-110"
                            title="Download Video"
                        >
                            <Film size={24} />
                        </button> */}

                        {/* <img
                            src={updateAdd}
                            title="Update"
                            alt="Update"
                            className="w-12 h-12 mx-4 rounded-lg duration-300 hover:scale-120"
                            onClick={handleUpdate.bind(null, "")}
                        />                

                        <img
                            src={downloadAudio}
                            title="Download Audio"
                            alt="Download Audio"
                            className="w-12 h-12 mx-4 rounded-lg duration-300 hover:scale-120"
                            onClick={handleDownload.bind(null, "audio")}
                        />                

                        <img
                            src={downloadVideo}
                            title="Download Video"
                            alt="Download Video"
                            className="w-12 h-12 mx-4 rounded-lg duration-300 hover:scale-120"
                            onClick={handleDownload.bind(null, "video")}
                        />                 */}
                    </div>

                    <div className='relative text-sm mt-3 mb-4 text-orange-300'>
                        <div>
                            Clipboard:
                        </div>
                        <div className='text-xs text-orange-300'>
                            {clipboardText?.startsWith(config.videoBaseUrl)
                            || clipboardText?.startsWith(config.videoLiveUrl)
                            || clipboardText?.startsWith(config.videoShareUrl)
                                ? <>
                                    <div className='text-nowrap truncate pr-3'>
                                        {clipboardText} 
                                    </div>
                                    <button
                                        type="button"
                                        onClick={clearClipboard}
                                        className="absolute inset-y-0 right-0 flex items-center text-sm leading-5"
                                    >
                                        <XMarkIcon className="h-4 w-4 mt-5 hover:bg-white/20 hover:scale-110" />
                                    </button>
                                </>
                                : '<Copy a link from YouTube>'
                            }
                        </div>
                    </div>

                    <FormField
                        label={"Keyword"}
                        name={"keyword"}
                        type={"text"}
                        value={user.keyword}
                        onChange={handleChange}
                        onClear={handleClear.bind(null, "keyword")}
                    />
                    <div className='flex justify-center'>
                        <ButtonIcon
                            icon={Search}
                            title={'Search keyword'}
                            bouncing={searching}
                            onClick={ async () => {
                                setSearching(true);
                                await handleSearch();
                            }}
                        />
                    </div>
                </Form>

                <div className='flex custom-hidden'>
                    <ImageGallery images={videoList} setFilespec={setFilespec} />
                    {/* {(videoList?.length > 0) 
                        ? <ImageGallery images={videoList} setFilespec={setFilespec} />
                        : <></>
                    } */}
                </div>

                <div className='flex custom-hidden'>
                    <PlaylistGallery
                        dailyList={dailyList}
                        heartList={heartList}
                        playlists={playlists}
                        crackList={crackList}
                        onResize={handlePlaylistGalleryResize}
                        setFilespec={setFilespec}
                        refreshPlaylists={refreshPlaylists}
                    />

                    {/* {(videoList?.length > 0) ?
                        <PlaylistGallery
                            dailyList={dailyList}
                            heartList={heartList}
                            playlists={playlists}
                            crackList={crackList}
                            onResize={handlePlaylistGalleryResize}
                            setFilespec={setFilespec}
                            refreshPlaylists={refreshPlaylists}
                        />
                    : <></>
                    } */}
                </div>
            </div>

            {/* <YouTubePlayer
                video_id={filespec?.video_id}
                onVideoEnd={handleVideoEnd}
            /> */}
        </Layout>
    )
}

